/**
 * Utility functions for date formatting and manipulation
 * Centralized date utilities for consistent formatting across the application
 */

/**
 * Format a Date as YYYY-MM-DD in the given timezone (or browser-local if omitted).
 * Replaces the common `date.toISOString().split('T')[0]` pattern which
 * incorrectly returns the UTC date instead of the user's local date.
 */
export function toLocalDateString(d: Date, timeZone?: string): string {
  if (timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(d);
    const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
    return `${map.year}-${map.month}-${map.day}`;
  }
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse a date-only string (YYYY-MM-DD) as a local-timezone Date.
 * `new Date('2026-06-30')` treats the value as UTC midnight, which shifts
 * the displayed day backwards in western timezones.  Appending `T00:00:00`
 * forces the parser to treat it as local midnight instead.
 */
export function parseLocalDate(dateString: string): Date {
  const dateOnly = dateString.split('T')[0];
  return new Date(dateOnly + 'T00:00:00');
}

/**
 * Whole calendar days from today until a date-only value (negative if past).
 *
 * THE canonical countdown for election dates and any other date-only
 * milestone. Every surface counting down to the same date must produce the
 * same number and the same weekday, so the parse and the diff live together
 * here. The 2026-08-26 election-countdown bug was exactly this calculation
 * duplicated three ways: two surfaces parsed date-only and said
 * "Tuesday, Nov 3 · 69 days" while the Pro home dashboard ran parseISO over
 * the API's UTC-midnight serialization ("2026-11-03T00:00:00.000Z"), which
 * in Denver renders as the previous evening — "Monday, Nov 2 · 68 days", a
 * general election on a Monday.
 */
export function daysUntilLocalDate(dateString: string | null | undefined): number | null {
  if (!dateString) return null;
  const target = parseLocalDate(dateString);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

/**
 * The one blank-value marker for user-facing display. Before this constant,
 * '—' (79 uses), '-' (51) and 'N/A' (39) all shipped for the same meaning.
 * The em dash is the typographic standard and was already the most common.
 */
export const EMPTY_VALUE = '—';

/**
 * Format a date to the app's canonical display format: "Jan 15, 2024".
 *
 * This is THE date display for the Pro web app — 42 files each defined a
 * local formatDate with drifting output (short/long/numeric month) and,
 * worse, most parsed date-only strings with `new Date(s)`, which reads them
 * as UTC midnight and displays the previous day in Mountain time.
 * formatConventions.test.ts bans new local definitions.
 *
 * @example
 * formatDate('2024-01-15') // 'Jan 15, 2024'
 * formatDate(null) // '—'
 */
export function formatDate(
  dateString: string | Date | undefined | null,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!dateString) return EMPTY_VALUE;

  try {
    const date = typeof dateString === 'string' ? parseLocalDate(dateString) : dateString;
    if (isNaN(date.getTime())) return EMPTY_VALUE;
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    };

    return date.toLocaleDateString('en-US', defaultOptions);
  } catch {
    return typeof dateString === 'string' ? dateString : EMPTY_VALUE;
  }
}

/**
 * Canonical date-with-time display: "Jan 15, 2024, 3:42 PM".
 * Timestamps carry real time zones, so no parseLocalDate here.
 *
 * Renders in the BROWSER's timezone, deliberately (ruled 2026-08-01).
 * The organization's timezone setting exists to fix EXPORTED reports,
 * which used to render UTC — a generated artifact has no browser to
 * localize it. On-screen display and exports are different problems:
 * do not wire the org setting into this function.
 */
export function formatDateTime(dateString: string | Date | undefined | null): string {
  if (!dateString) return EMPTY_VALUE;
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return EMPTY_VALUE;
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Compact numeric date: "06/26/26". For dense tables where even
 * "Jun 26, 2026" costs too much width; everywhere else prefer formatDate.
 */
export function formatDateShort(dateString: string | Date | undefined | null): string {
  return formatDate(dateString, { year: '2-digit', month: '2-digit', day: '2-digit' });
}

/**
 * Time alone: "3:42 PM". Pairs with formatDate/formatDateShort for compact
 * two-line timestamps (date over time) where the one-line formatDateTime
 * wraps. Browser-local, same ruling as formatDateTime.
 */
export function formatTime(dateString: string | Date | undefined | null): string {
  if (!dateString) return EMPTY_VALUE;
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return EMPTY_VALUE;
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/**
 * Format a date string to a long format (e.g., "January 15, 2024")
 *
 * @param dateString - ISO date string or date-like string
 * @returns Formatted date string or fallback
 */
export function formatDateLong(dateString: string | undefined | null): string {
  return formatDate(dateString, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Get a date range for a specified number of days ago
 * 
 * @param daysAgo - Number of days in the past (default: 7)
 * @returns Object with startDate and endDate as ISO strings (YYYY-MM-DD)
 * 
 * @example
 * getDateRange(7) // { startDate: '2024-01-08', endDate: '2024-01-15' }
 */
export function getDateRange(daysAgo: number = 7): { startDate: string; endDate: string } {
  const today = new Date();
  const pastDate = new Date(today);
  pastDate.setDate(today.getDate() - daysAgo);
  
  return {
    startDate: toLocalDateString(pastDate),
    endDate: toLocalDateString(today)
  };
}

/**
 * Check if a date is within a specified range
 * 
 * @param date - Date to check
 * @param startDate - Start of range
 * @param endDate - End of range
 * @returns True if date is within range
 */
export function isDateInRange(
  date: Date | string,
  startDate: Date | string,
  endDate: Date | string
): boolean {
  const checkDate = typeof date === 'string' ? new Date(date) : date;
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
  
  return checkDate >= start && checkDate <= end;
}

